<template>
  <div class="sticky inset-x-0 bottom-0 border-t border-t-grey-300 bg-white py-3 tablet:py-12">
    <div class="grid-container">
      <div class="col-span-12 flex items-center justify-between">
        <div>
          <button v-if="canGoBack" class="btn-link-neutral-m tablet:btn-link-neutral-l" @click.stop="$emit('previousStep')">
            {{ $t('create.previous_step') }}
          </button>

          <div v-if="!canGoBack && showPrice">
            <div class="flex items-center gap-4">
              <TextInputBase id="promoCode" v-model="promoCode" :placeholder="$t('create.promo_code.placeholder').toString()" />

              <button class="btn-primary-neutral-s tablet:btn-primary-neutral-m" @click="setPromoCode">
                {{ $t('create.promo_code.apply') }}
              </button>
            </div>

            <p class="text-sm">{{ promoCodeFeedback }}</p>
          </div>
        </div>

        <div class="flex shrink-0 basis-52 items-center justify-end space-x-3 tablet:basis-auto tablet:space-x-6">
          <div v-if="showPrice && jobOfferPrice" class="text-right">
            <h2 class="text-headline-l leading-none italic">
              {{ formattedPrice }}
            </h2>

            <div class="text-sm tablet:text-base">
              {{ $t('create.netto') }}
            </div>
          </div>

          <button class="btn-primary-color-s tablet:btn-primary-color-m" :disabled="nextStepDisabled" @click.stop="proceedToNext">
            {{ finalStep ? $t('create.publish') : $t('create.next_step') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { TextInputBase } from '@ramp106/omrjs-core-ui/deprecated'
import type { Validation } from '@vuelidate/core'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { priceWithDiscount } from '@/helpers/pricing'

import { useRuntimeConfig } from '#imports'
import { useJobsSdk } from '~/gql'
import { useNewJobStore } from '~/store/newJob'

const props = defineProps<{
  jobOfferPrice?: {
    price: number
    currencySymbol: string
  }
  showPrice?: boolean
  stepCount: number
  currentStepCount: number
  validator: Validation
}>()

const runtimeConfig = useRuntimeConfig()
const { getPromoCode } = useJobsSdk()
const store = useNewJobStore()
const { t } = useI18n()

const promoCodeFeedback = ref()
const promoCode = computed({
  get() {
    return store.promoCode
  },
  set(value) {
    store.updatePromoCode(value)
    promoCodeFeedback.value = ''
  },
})
const promoCodeDiscount = computed({
  get() {
    return store.promoCodeDiscount
  },
  set(value) {
    store.updatePromoCodeDiscount(value)
  },
})

const formattedPrice = computed(() => {
  if (!props.jobOfferPrice) return

  return `${priceWithDiscount(props.jobOfferPrice.price, promoCodeDiscount.value)} ${props.jobOfferPrice.currencySymbol}`
})

const setPromoCode = async () => {
  const result = await getPromoCode(
    { name: promoCode.value },
    {
      fetchPolicy: 'network-only',
    },
  )
  const data = result.data?.promoCode

  if (data) {
    promoCode.value = data.name || ''
    promoCodeDiscount.value = data.discountPercentage
    promoCodeFeedback.value = `${promoCodeDiscount.value}${t('create.promo_code.feedback.valid')}`
    store.updateJobOffer({
      promoCodeId: data.id,
    })
  } else {
    promoCode.value = ''
    promoCodeFeedback.value = t('create.promo_code.feedback.invalid')
    promoCodeDiscount.value = 0
    store.removePromoCodeId()
  }
}

const emit = defineEmits(['nextStep', 'previousStep'])

const proceedToNext = () => {
  if (String(runtimeConfig.public.debugJobWizard) === 'true') {
    return emit('nextStep')
  }
  props.validator.$touch()

  if (!props.validator.$invalid) {
    emit('nextStep')
  }
}

const nextStepDisabled = computed(() => props.validator.$error && props.validator.$invalid)
const canGoBack = computed(() => props.currentStepCount > 1)
const finalStep = computed(() => props.currentStepCount === props.stepCount)
</script>
